/**
 * WorkflowTools — Configuration management page for agent tools.
 *
 * Features a "Quick Connect" section with pre-built provider cards for common
 * data sources, plus the existing namespace-grouped config table for custom entries.
 */

import { PROVIDERS } from "./workflowToolsProviders";
import { WorkflowConfigTable } from "./WorkflowConfigTable";
import { WorkflowProviderConnectForm } from "./WorkflowProviderConnectForm";
import { WorkflowConfigEntryForm } from "./WorkflowConfigEntryForm";
import { useWorkflowToolsState } from "./useWorkflowToolsState";
import styles from "./workflow.module.scss";

const WorkflowTools = () => {
  const {
    isLoading, error, configs, grouped, allNamespaces, getProviderConfigCount,
    showForm, setShowForm, editingEntry, formNs, formCustomNs, formKey, formValue,
    formEncrypted, revealedIds, activeProvider, providerValues, providerSaving,
    setFormNs, setFormCustomNs, setFormKey, setFormValue, setFormEncrypted,
    setActiveProvider, setProviderValues, resetForm, handleSubmit, handleEdit,
    handleDelete, toggleReveal, openProviderForm, closeProviderForm, handleProviderSubmit,
    upsertMutation, deleteMutation,
  } = useWorkflowToolsState();

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <span>Loading configurations...</span>
      </div>
    );
  }

  if (error) {
    return <div className={styles.errorState}>Failed to load configurations: {String(error)}</div>;
  }

  return (
    <div className={styles.toolsPage}>
      <div className={styles.toolsHeader}>
        <div>
          <h2 className={styles.toolsTitle}>Tool Configuration</h2>
          <p className={styles.toolsSubtitle}>
            Connect to data sources, manage API keys, and configure agent runtime settings.
          </p>
        </div>
        <button
          className={styles.addConfigBtn}
          onClick={() => { setActiveProvider(null); resetForm(); setShowForm(true); }}
        >
          + Add Custom Config
        </button>
      </div>

      {/* Quick Connect Section */}
      <div className={styles.quickConnectSection}>
        <div className={styles.quickConnectHeader}>
          <h3 className={styles.quickConnectTitle}>Quick Connect</h3>
          <p className={styles.quickConnectSubtitle}>
            Select a data source to set up a connection with pre-filled fields.
          </p>
        </div>
        <div className={styles.providerGrid}>
          {PROVIDERS.map(provider => {
            const configCount = getProviderConfigCount(provider);
            const totalFields = provider.fields.length;
            const isConfigured = configCount === totalFields;
            const isActive = activeProvider?.id === provider.id;
            return (
              <button key={provider.id} type="button"
                className={`${styles.providerCard} ${isActive ? styles.providerCardActive : ""} ${isConfigured ? styles.providerCardConfigured : ""}`}
                style={{ "--provider-color": provider.color, "--provider-bg": provider.bgColor } as React.CSSProperties}
                onClick={() => openProviderForm(provider)}
              >
                <div className={styles.providerIcon}>{provider.icon}</div>
                <div className={styles.providerInfo}>
                  <span className={styles.providerName}>{provider.name}</span>
                  <span className={styles.providerDesc}>{provider.description}</span>
                </div>
                {isConfigured && <span className={styles.providerStatusBadge} data-status="connected">Connected</span>}
                {!isConfigured && configCount > 0 && <span className={styles.providerStatusBadge} data-status="partial">{configCount}/{totalFields}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Provider Connect Form */}
      {activeProvider && (
        <WorkflowProviderConnectForm
          provider={activeProvider}
          values={providerValues}
          saving={providerSaving}
          onClose={closeProviderForm}
          onSubmit={handleProviderSubmit}
          onChange={(key, value) => setProviderValues(prev => ({ ...prev, [key]: value }))}
        />
      )}

      {/* Generic Add/Edit form */}
      {showForm && (
        <WorkflowConfigEntryForm
          editingEntry={editingEntry}
          formNs={formNs}
          formCustomNs={formCustomNs}
          formKey={formKey}
          formValue={formValue}
          formEncrypted={formEncrypted}
          saving={upsertMutation.isPending}
          onSubmit={handleSubmit}
          onCancel={resetForm}
          onNsChange={setFormNs}
          onCustomNsChange={setFormCustomNs}
          onKeyChange={setFormKey}
          onValueChange={setFormValue}
          onEncryptedChange={setFormEncrypted}
        />
      )}

      {/* Separator */}
      <div className={styles.configSectionDivider}>
        <span>All Configurations</span>
      </div>

      {/* Namespace cards */}
      <WorkflowConfigTable
        allNamespaces={allNamespaces}
        grouped={grouped}
        revealedIds={revealedIds}
        onToggleReveal={toggleReveal}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isDeletePending={deleteMutation.isPending}
      />

      {configs.length === 0 && !showForm && !activeProvider && (
        <div className={styles.emptyState}>
          <span>No configurations found.</span>
          <span>Use Quick Connect above or click &quot;Add Custom Config&quot; to get started.</span>
        </div>
      )}
    </div>
  );
};

export default WorkflowTools;
